import { useEffect, useMemo, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { ApiError, api, quarterLabel, type AvailabilityQuarter, type MyRequest, type PhysicianSummary } from '../lib/api';
import { addDays, dayLabel } from '../lib/dates';

/**
 * A physician's availability board for a quarter (D29, D32, D58). While the
 * scheduler's request window is open it is the request form: each Tuesday
 * week Want or Can't, how many weeks they are aiming for, a note. After the
 * window closes it stays live: a week they are not scheduled in can still be
 * changed at any time, because a locum who is not needed books elsewhere and
 * the scheduler must know at once — no reason asked. A week they are
 * scheduled in is locked.
 *
 * TWO answers, not three (D58). There used to be an "Available" in the
 * middle, and leaving a week on it meant the office could call on you. James
 * removed it: a week is Want, Can't, or not answered yet — and not answered
 * means the drafter will not place you in it. So an unmarked week is shown as
 * an unanswered one, plainly, rather than as a quiet yes.
 */
type Choice = 'want' | 'blackout';

/** The days of a Tuesday-start week, for the part-week day picker (D60). */
const daysOf = (week: string) => Array.from({ length: 7 }, (_, i) => addDays(week, i));

export function Requests({ physician }: { physician: PhysicianSummary }) {
  const [quarters, setQuarters] = useState<AvailabilityQuarter[] | null>(null);
  const [quarter, setQuarter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .availabilityQuarters(physician.abbreviation)
      .then((qs) => {
        setQuarters(qs);
        // Open the quarter whose request window is open (nearest deadline first); otherwise the earliest one.
        const open = qs.filter((q) => q.window?.open).sort((a, b) => a.window!.closesOn.localeCompare(b.window!.closesOn));
        setQuarter((open[0] ?? qs[0])?.quarter ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, [physician.abbreviation]);

  if (error) return <p className="error">Couldn't load your availability ({error}).</p>;
  if (!quarters || !quarter) return <p className="muted">Loading…</p>;
  return (
    <section className="reqpage">
      <div className="tabs" role="tablist">
        {quarters.map((q) => (
          <button key={q.quarter} type="button" role="tab" aria-selected={q.quarter === quarter} className={q.quarter === quarter ? 'tab active' : 'tab'} onClick={() => setQuarter(q.quarter)}>
            {quarterLabel(q.quarter)}
            {q.window?.open && <span className="pill tiny">requests open</span>}
          </button>
        ))}
      </div>
      <Board key={`${physician.abbreviation}|${quarter}`} physician={physician} quarter={quarter} />
    </section>
  );
}

function Board({ physician, quarter }: { physician: PhysicianSummary; quarter: string }) {
  const [data, setData] = useState<MyRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice | undefined>>({});
  // Part-week offers, keyed by the week's Tuesday (D60). A week is either answered whole
  // (Want / Can't) or offered in part — never both, which the API also refuses.
  const [partials, setPartials] = useState<Record<string, { dates: string[]; note: string }>>({});
  const [picking, setPicking] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('');
  const [note, setNote] = useState('');
  const [blockWeeks, setBlockWeeks] = useState('');
  const [maxInRow, setMaxInRow] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = () =>
    api
      .myRequest(physician.abbreviation, quarter)
      .then((d) => {
        setData(d);
        const c: Record<string, Choice> = {};
        // A week they have not answered has no entry at all — which is what "unanswered" is.
        for (const w of d.weeks) {
          if (d.request?.wantedWeeks.includes(w)) c[w] = 'want';
          else if (d.request?.blackoutWeeks.includes(w)) c[w] = 'blackout';
        }
        setChoices(c);
        const ps: Record<string, { dates: string[]; note: string }> = {};
        for (const p of d.partials ?? []) ps[p.week] = { dates: p.dates, note: p.note ?? '' };
        setPartials(ps);
        setPicking(null);
        setTarget(d.request?.targetWeeks == null ? '' : String(d.request.targetWeeks));
        setNote(d.request?.note ?? '');
        setBlockWeeks(d.request?.blockWeeks == null ? '' : String(d.request.blockWeeks));
        setMaxInRow(d.request?.maxConsecutiveWeeks == null ? '' : String(d.request.maxConsecutiveWeeks));
        setDirty(false);
      })
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    load();
  }, [physician.abbreviation, quarter]);
  useReloadOnChange(load);

  const counts = useMemo(() => {
    const vals = Object.values(choices);
    return { want: vals.filter((v) => v === 'want').length, blackout: vals.filter((v) => v === 'blackout').length };
  }, [choices]);

  if (error) return <p className="error">Couldn't load your availability ({error}).</p>;
  if (!data) return <p className="muted">Loading {quarterLabel(quarter)}…</p>;
  const requestsOpen = data.window?.open ?? false;
  const lockedBy = new Map(data.locked.map((l) => [l.week, l]));
  // Weeks already under way (D97). The board stays live after the window shuts so somebody can
  // still mark a week Can't when something comes up — which means nothing once the week has
  // begun. Shown with whatever was answered, and no buttons to change it.
  const startedWeeks = new Set(data.started ?? []);
  const first = data.weeks[0];
  const last = addDays(data.weeks[data.weeks.length - 1], 6);

  // Pressing the answer you already gave takes it back off, leaving the week unanswered.
  // With only two answers there has to be a way back to "I haven't said".
  const choose = (week: string, v: Choice) => {
    if (lockedBy.has(week) || startedWeeks.has(week)) return;
    setChoices((c) => {
      const next = { ...c };
      if (next[week] === v) delete next[week];
      else next[week] = v;
      return next;
    });
    // A whole-week answer replaces a part-week offer: they are answers to the same question.
    setPartials((p) => { const n = { ...p }; delete n[week]; return n; });
    if (picking === week) setPicking(null);
    setDirty(true);
    setSaved(null);
  };

  /** Offer part of a week (D60) — which also takes back any whole-week answer for it. */
  const togglePartial = (week: string) => {
    if (lockedBy.has(week) || startedWeeks.has(week)) return;
    if (partials[week]) {
      setPartials((p) => { const n = { ...p }; delete n[week]; return n; });
      setPicking(null);
    } else {
      setChoices((c) => { const n = { ...c }; delete n[week]; return n; });
      setPartials((p) => ({ ...p, [week]: { dates: [], note: '' } }));
      setPicking(week);
    }
    setDirty(true);
    setSaved(null);
  };

  const toggleDay = (week: string, date: string) => {
    setPartials((p) => {
      const cur = p[week] ?? { dates: [], note: '' };
      const has = cur.dates.includes(date);
      // Seven days is the whole week, which is Want — the form does not let them get there.
      const dates = has ? cur.dates.filter((d) => d !== date) : [...cur.dates, date].sort();
      if (dates.length === 7) return p;
      return { ...p, [week]: { ...cur, dates } };
    });
    setDirty(true);
    setSaved(null);
  };
  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const body = {
        wantedWeeks: data.weeks.filter((w) => choices[w] === 'want'),
        blackoutWeeks: data.weeks.filter((w) => choices[w] === 'blackout'),
        targetWeeks: target.trim() === '' ? null : Number(target),
        blockWeeks: blockWeeks.trim() === '' ? null : Number(blockWeeks),
        maxConsecutiveWeeks: maxInRow.trim() === '' ? null : Number(maxInRow),
        note: note.trim() || null,
        // Only offers with at least one day chosen; an empty one is somebody who opened the
        // picker and changed their mind, not an offer.
        partials: Object.entries(partials)
          .filter(([, v]) => v.dates.length > 0)
          .map(([week, v]) => ({ week, dates: v.dates, note: v.note.trim() || null })),
      };
      const r = await api.saveRequest(physician.abbreviation, quarter, body);
      setSaved(new Date(r.updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }));
      await load();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // What is still outstanding — the number the physician should act on, and the one the
  // office would otherwise have to chase them for.
  const partialCount = data.weeks.filter((w) => partials[w]?.dates.length).length;
  const unansweredCount = data.weeks.filter((w) => !lockedBy.has(w) && !startedWeeks.has(w) && !choices[w] && !partials[w]).length;

  return (
    <div>
      <div className="card">
        <h1>{quarterLabel(quarter)} {requestsOpen ? 'requests' : 'availability'}</h1>
        <p className="summary">
          {dayLabel(first).replace(/^\w+, /, '')} – {dayLabel(last).replace(/^\w+, /, '')}
          {data.window && requestsOpen && <> · please answer by <strong>{dayLabel(data.window.closesOn)}</strong></>}
          {data.window && !requestsOpen && <> · requests closed {dayLabel(data.window.closesOn)}</>}
        </p>
        {requestsOpen && data.window?.message && <p className="notice">{data.window.message}</p>}
        {!requestsOpen && (
          <p className="muted small">
            Once the schedule is built, the weeks you're scheduled in are locked here — a change to a scheduled shift goes through your scheduler, not the app. Every other week stays yours to keep current: if something comes up, mark it <strong>Can't</strong> right away, so we don't count on you as a reserve for switches that week. Your scheduler sees the change immediately.
          </p>
        )}
        {requestsOpen && (
          <p className="muted small">
            Mark every week you could work as <strong>Want</strong> — those are the only ones we can schedule you into. A week you leave blank reads as "no answer yet", not as a yes, so we'll come and ask. Press the same button again to take an answer back. You can change any unscheduled week later; once the schedule is built your scheduled weeks lock and changes to those go through your scheduler.
          </p>
        )}
        {data.request && !dirty && requestsOpen && (
          <p className="ok small">Submitted {new Date(data.request.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. You can change it any time before the closing date.</p>
        )}
      </div>

      <ul className="weeks">
        {data.weeks.map((w, i) => {
          const lock = lockedBy.get(w);
          const state = choices[w];
          const part = partials[w];
          return (
            <li key={w} className={`week-row ${lock ? 'locked' : part ? 'partial' : state ?? 'unanswered'}`}>
              <div className="week-label">
                <span className="week-dates">{weekRange(w)}</span>
                <span className="muted small">wk {i + 1} · Tue–Mon</span>
              </div>
              {lock ? (
                <div className="lockinfo">
                  <span className={`site site-${lock.siteId}`}>{lock.siteName}</span>
                  <span className="muted small">{lock.status === 'published' ? 'scheduled' : 'penciled in — awaiting publication'}</span>
                </div>
              ) : startedWeeks.has(w) ? (
                <div className="lockinfo">
                  <span className="muted">{state === 'want' ? 'Wanted' : state === 'blackout' ? "Couldn't" : part ? 'Offered part of it' : 'Not answered'}</span>
                  <span className="muted small">this week has started</span>
                </div>
              ) : (
                <div className="choice" role="group" aria-label={`Week of ${w}`}>
                  <button type="button" aria-pressed={state === 'want'} className={state === 'want' ? 'on want' : ''} onClick={() => choose(w, 'want')}>Want</button>
                  <button type="button" aria-pressed={state === 'blackout'} className={state === 'blackout' ? 'on blackout' : ''} onClick={() => choose(w, 'blackout')}>Can't</button>
                  <button type="button" aria-pressed={!!part} className={part ? 'on partial' : ''} onClick={() => togglePartial(w)}>Part of it</button>
                  {!state && !part && <span className="muted small nochoice">not answered</span>}
                </div>
              )}
              {part && (
                <div className="partialpick">
                  {picking === w || part.dates.length === 0 ? (
                    <>
                      <p className="muted small">Which days could you cover? The office decides whether to use it — you won't be scheduled from this on its own.</p>
                      <div className="daypick" role="group" aria-label={`Days you could work the week of ${w}`}>
                        {daysOf(w).map((d) => (
                          <button key={d} type="button" aria-pressed={part.dates.includes(d)} className={part.dates.includes(d) ? 'on' : ''} onClick={() => toggleDay(w, d)}>
                            {dayLabel(d).replace(/^(\w{3})\w*, (\w{3})\w* (\d+).*$/, '$1 $3')}
                          </button>
                        ))}
                      </div>
                      <label className="field grow">
                        <span>Anything the office should know?</span>
                        <input
                          type="text"
                          maxLength={500}
                          value={part.note}
                          placeholder="e.g. flying in Thursday morning"
                          onChange={(e) => { const v = e.target.value; setPartials((ps) => ({ ...ps, [w]: { ...ps[w], note: v } })); setDirty(true); setSaved(null); }}
                        />
                      </label>
                      {part.dates.length > 0 && (
                        <button type="button" className="small" onClick={() => setPicking(null)}>Done with this week</button>
                      )}
                    </>
                  ) : (
                    <p className="small">
                      <strong>{part.dates.length} day{part.dates.length === 1 ? '' : 's'} offered</strong>
                      <span className="muted"> · {part.dates.map((d) => dayLabel(d).replace(/^(\w{3})\w*,.*?(\d+)(?:,.*)?$/, '$1 $2')).join(', ')}</span>
                      {part.note && <span className="muted"> — {part.note}</span>}
                      <button type="button" className="linky" onClick={() => setPicking(w)}>change</button>
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="card">
        {requestsOpen && (
          <>
            <label className="field">
              <span>How many weeks are you aiming for this quarter?</span>
              <input type="number" inputMode="numeric" min={0} max={14} value={target} onChange={(e) => { setTarget(e.target.value); setDirty(true); setSaved(null); }} placeholder="e.g. 6" />
            </label>
            {/* Two answers the office has never had, and the schedule is built from (D42). */}
            <div className="two">
              <label className="field">
                <span>When you come, how many weeks in a row do you like to work?</span>
                <input type="number" inputMode="numeric" min={1} max={4} value={blockWeeks} onChange={(e) => { setBlockWeeks(clamp(e.target.value, 1, 4)); setDirty(true); setSaved(null); }} placeholder="usually 1" />
              </label>
              <label className="field">
                <span>Most weeks in a row you'll take</span>
                <input type="number" inputMode="numeric" min={1} max={12} value={maxInRow} onChange={(e) => { setMaxInRow(clamp(e.target.value, 1, 12)); setDirty(true); setSaved(null); }} placeholder="e.g. 2" />
              </label>
            </div>
          </>
        )}
        <label className="field">
          <span>Anything your scheduler should know</span>
          <textarea rows={3} value={note} maxLength={2000} onChange={(e) => { setNote(e.target.value); setDirty(true); setSaved(null); }} placeholder="e.g. Away Jun 14–20; prefer Randolph; happy to take nights" />
        </label>
        <p className="muted small">
          {data.locked.length > 0 && <>{data.locked.length} scheduled · </>}{counts.want} wanted · {counts.blackout} can't work · {partialCount > 0 ? <>{partialCount} part-week · </> : null}{unansweredCount} not answered
        </p>
        {problem && <p className="error">{problem}</p>}
        <div className="formactions">
          <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>{busy ? 'Saving…' : requestsOpen ? (data.request ? 'Save changes' : 'Submit request') : 'Update availability'}</button>
          {saved && <span className="ok small">Saved {saved}</span>}
          {dirty && !saved && <span className="muted small">Not saved yet</span>}
        </div>
      </div>

      {data.changes.length > 0 && (
        <details className="history small">
          <summary>Your changes ({data.changes.length})</summary>
          <ul>
            {data.changes.map((c, i) => (
              <li key={i}>
                {new Date(c.changedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · week of {dayLabel(c.week).replace(/^\w+, /, '')}: {word(c.from)} → {word(c.to)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function word(s: string): string {
  if (s === 'blackout') return 'not available';
  if (s === 'want') return 'want';
  // Change rows written before D58 can say 'available' — the third answer that used to exist.
  // Rendering those as "no answer yet" would rewrite what the physician actually did.
  if (s === 'available') return 'available (the old third answer)';
  return 'no answer yet';
}

/** "Apr 20 – 26", or "Mar 30 – Apr 5" across a month boundary. */
function weekRange(tuesday: string): string {
  const start = dayLabel(tuesday).replace(/^\w+, /, '');
  const end = dayLabel(addDays(tuesday, 6)).replace(/^\w+, /, '');
  return start.split(' ')[0] === end.split(' ')[0] ? `${start} – ${end.split(' ')[1]}` : `${start} – ${end}`;
}

/** Keep a typed number inside what the API accepts, so a slip never rejects the whole board. */
function clamp(raw: string, min: number, max: number): string {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  return String(Math.min(max, Math.max(min, Math.round(n))));
}
